import React, { useEffect, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../../hooks/useUserData";
import { getQmsScreen } from "./qmsScreenConfig";
import { probeQmsScreen } from "./qmsService";
import { getQmsAccessState } from "./qmsPermissionUtils";
import "./QmsPages.scss";

/**
 * Scaffold shell: verifies API connectivity for a screen (no static/mock data).
 */
const QmsPageShell = ({ screenKey, children }) => {
  const { userData, loading: userLoading } = useUserData();
  const screen = getQmsScreen(screenKey);
  const isSystemAdmin = userData?.Role === "System Administrator";

  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: screen?.permissionKey,
    loading: userLoading,
  });

  const [apiState, setApiState] = useState({
    status: "idle",
    message: "",
  });

  useEffect(() => {
    if (
      access.status !== "allowed" ||
      !screen ||
      !userData?.OrganizationId ||
      !userData?.StaffId
    ) {
      return;
    }

    let cancelled = false;
    setApiState({ status: "loading", message: "" });

    (async () => {
      try {
        const result = await probeQmsScreen(screenKey, userData);
        if (cancelled) return;
        setApiState({
          status: result.ok ? "ready" : "error",
          message:
            result.message ||
            (result.ok ? "API connected." : "API returned an error."),
        });
      } catch (err) {
        if (cancelled) return;
        setApiState({
          status: "error",
          message: err?.message || "Failed to reach QMS API.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.status, screenKey, userData?.OrganizationId, userData?.StaffId]);

  if (!screen) {
    return (
      <div className="user-mgmt-page qms-page-shell">
        <p className="text-danger">Unknown QMS screen.</p>
      </div>
    );
  }

  if (access.status === "loading") {
    return null;
  }

  const breadcrumb = (
    <PageBreadcrumb
      items={[
        { label: "Home", path: "/notification-center" },
        { label: screen.title },
      ]}
    />
  );

  if (access.status === "denied") {
    return (
      <div className="user-mgmt-page qms-page-shell">
        {breadcrumb}
        <div className="alert alert-warning" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-page-shell">
      {breadcrumb}

      {apiState.status === "loading" && (
        <div className="text-muted py-3" role="status">
          Loading {screen.title}…
        </div>
      )}

      {apiState.status === "error" && (
        <div className="alert alert-danger" role="alert">
          {apiState.message}
        </div>
      )}

      {apiState.status === "ready" && (
        <div
          className="alert alert-success qms-page-shell__api-ok"
          role="status"
        >
          {apiState.message} Screen UI will be built in the next phase.
        </div>
      )}

      {children}
    </div>
  );
};

export default QmsPageShell;
